import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useProject } from "../contexts/ProjectContext";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { ArrowLeft, Plus, Edit, Trash2, Brain, Search } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const SystemPrompts = () => {
  const { systemPrompts, settings, createSystemPrompt, updateSystemPrompt, deleteSystemPrompt } = useProject();
  const { toast } = useToast();
  
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newSystemPrompt, setNewSystemPrompt] = useState({ name: "", content: "" });
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [createButtonState, setCreateButtonState] = useState("idle"); // idle, creating, created
  const [updateButtonState, setUpdateButtonState] = useState("idle"); // idle, updating, updated
  const [searchQuery, setSearchQuery] = useState("");

  // Filter system prompts based on search query
  const filteredSystemPrompts = useMemo(() => {
    if (!searchQuery.trim()) {
      return systemPrompts;
    }
    
    const query = searchQuery.toLowerCase();
    return systemPrompts.filter(prompt => 
      prompt.name.toLowerCase().includes(query) || 
      prompt.content.toLowerCase().includes(query)
    );
  }, [systemPrompts, searchQuery]);

  const handleCreateSystemPrompt = async () => {
    if (!newSystemPrompt.name.trim() || !newSystemPrompt.content.trim()) {
      toast({
        title: "Error",
        description: "Both name and content are required",
        variant: "destructive"
      });
      return;
    }

    setCreateButtonState("creating");
    try {
      await createSystemPrompt(newSystemPrompt);
      setNewSystemPrompt({ name: "", content: "" });
      setCreateButtonState("created");
      
      // Show success state briefly
      setTimeout(() => {
        setIsCreateDialogOpen(false);
        setCreateButtonState("idle");
      }, 1000);
      
      toast({
        title: "Success",
        description: "System prompt created successfully"
      });
    } catch (error) {
      setCreateButtonState("idle");
      toast({
        title: "Error",
        description: "Failed to create system prompt",
        variant: "destructive"
      });
    }
  };

  const handleUpdateSystemPrompt = async () => {
    if (!editingPrompt.name.trim() || !editingPrompt.content.trim()) {
      toast({
        title: "Error",
        description: "Both name and content are required",
        variant: "destructive"
      });
      return;
    }

    setUpdateButtonState("updating");
    try {
      await updateSystemPrompt(editingPrompt.id, editingPrompt);
      setUpdateButtonState("updated");
      
      // Show success state briefly
      setTimeout(() => {
        setIsEditDialogOpen(false);
        setEditingPrompt(null);
        setUpdateButtonState("idle");
      }, 1000);
      
      toast({
        title: "Success",
        description: "System prompt updated successfully"
      });
    } catch (error) {
      setUpdateButtonState("idle");
      
      // Extract error message from backend response
      let errorMessage = "Failed to update system prompt";
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const handleDeleteSystemPrompt = async (systemPromptId, name) => {
    const systemPrompt = systemPrompts.find(sp => sp.id === systemPromptId);
    if (systemPrompt?.is_default) {
      toast({
        title: "Cannot Delete",
        description: "Cannot delete the default system prompt",
        variant: "destructive"
      });
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await deleteSystemPrompt(systemPromptId);
        toast({
          title: "Deleted",
          description: "System prompt deleted successfully"
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete system prompt",
          variant: "destructive"
        });
      }
    }
  };

  const openEditDialog = (systemPrompt) => {
    setEditingPrompt({ ...systemPrompt });
    setUpdateButtonState("idle"); // Reset button state when opening dialog
    setIsEditDialogOpen(true);
  };

  const handleSystemPromptClick = (systemPrompt) => {
    openEditDialog(systemPrompt);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-800">System Prompts</h1>
                  <p className="text-sm text-slate-500">Manage AI improvement templates</p>
                </div>
              </div>
            </div>
            <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
              setIsCreateDialogOpen(open);
              if (open) {
                setCreateButtonState("idle"); // Reset button state when opening dialog
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  New System Prompt
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create System Prompt</DialogTitle>
                  <DialogDescription>
                    Create a new system prompt template for AI improvement
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="e.g. Creative Enhancement"
                      value={newSystemPrompt.name}
                      onChange={(e) => setNewSystemPrompt({ ...newSystemPrompt, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="content">System Prompt Content</Label>
                    <Textarea
                      id="content"
                      placeholder="Write your system prompt instructions here..."
                      rows={10}
                      value={newSystemPrompt.content}
                      onChange={(e) => setNewSystemPrompt({ ...newSystemPrompt, content: e.target.value })}
                    />
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleCreateSystemPrompt}
                      disabled={createButtonState !== "idle"}
                      className={createButtonState === "created" ? "bg-green-600 hover:bg-green-700" : ""}
                    >
                      {createButtonState === "creating" && "Creating..."}
                      {createButtonState === "created" && "Created!"}
                      {createButtonState === "idle" && "Create System Prompt"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-800 mb-2">System Prompt Templates</h2>
          <p className="text-slate-600">
            These templates guide how AI improves your prompts. Create custom templates for different types of enhancements.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search system prompts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border-slate-200 focus:border-violet-300 focus:ring-violet-300"
            />
          </div>
        </div>

        {/* System Prompts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredSystemPrompts.length > 0 ? (
            filteredSystemPrompts.map((systemPrompt) => (
              <Card 
                key={systemPrompt.id} 
                className="group hover:shadow-lg transition-all duration-200 border-slate-200 hover:border-violet-300 cursor-pointer"
                onClick={() => handleSystemPromptClick(systemPrompt)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="text-lg font-semibold text-slate-800">
                          {systemPrompt.name}
                        </CardTitle>
                        {(systemPrompt.is_default || systemPrompt.id === settings.default_system_prompt) && (
                          <Badge variant="secondary" className="text-xs">
                            Default
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="text-slate-500">
                        Created {new Date(systemPrompt.created_at).toLocaleDateString()} • Click to edit
                      </CardDescription>
                    </div>
                    <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(systemPrompt);
                        }}
                        className="text-slate-400 hover:text-violet-600"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {!systemPrompt.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSystemPrompt(systemPrompt.id, systemPrompt.name);
                          }}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 max-h-32 overflow-y-auto">
                    {systemPrompt.content}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-1 lg:col-span-2 text-center py-12">
              <div className="text-slate-400 mb-4">
                <Search className="w-12 h-12 mx-auto mb-3" />
                <p className="text-lg font-medium">No system prompts found</p>
                <p className="text-sm">
                  {searchQuery ? `No results for "${searchQuery}"` : "Create your first system prompt to get started"}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Edit Dialog */}
        {editingPrompt && (
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit System Prompt</DialogTitle>
                <DialogDescription>
                  Update your system prompt template
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-name">Name</Label>
                  <Input
                    id="edit-name"
                    value={editingPrompt.name}
                    onChange={(e) => setEditingPrompt({ ...editingPrompt, name: e.target.value })}
                    disabled={editingPrompt.is_default}
                  />
                  {editingPrompt.is_default && (
                    <p className="text-xs text-slate-500 mt-1">Default system prompt name cannot be changed</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="edit-content">System Prompt Content</Label>
                  <Textarea
                    id="edit-content"
                    rows={10}
                    value={editingPrompt.content}
                    onChange={(e) => setEditingPrompt({ ...editingPrompt, content: e.target.value })}
                  />
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUpdateSystemPrompt}
                    disabled={updateButtonState !== "idle"}
                    className={updateButtonState === "updated" ? "bg-green-600 hover:bg-green-700" : ""}
                  >
                    {updateButtonState === "updating" && "Updating..."}
                    {updateButtonState === "updated" && "Updated!"}
                    {updateButtonState === "idle" && "Update System Prompt"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </main>
    </div>
  );
};

export default SystemPrompts;